<template>
    <div class="q-pa-md">
        <div class="column q-col-gutter-md">
            <div class="row q-col-gutter-x-lg">
                <!-- first name -->
                <div class="col-12 col-md-6 q-pb-lg">
                    <q-input
                        v-model="user.first_name"
                        :label="$t('general.name')"
                        type="text"
                        bg-color="white"
                        outlined
                        clearable
                        counter
                        maxlength="35"
                        :error-message="firstNameErrors"
                        :error="firstNameErrors != ''"
                        @input="$v.user.first_name.$touch()"
                        @blur="$v.user.first_name.$touch()"
                    />
                </div>
                <!-- gender -->
                <div class="col-12 col-md-3 q-pb-lg">
                    <q-select
                        v-model="user.gender_id"
                        class="q-mb-md"
                        :label="$t('general.gender')"
                        bg-color="white"
                        hide-selected
                        outlined
                        input-debounce="0"
                        option-value="id"
                        option-label="name"
                        map-options
                        :options="genderOptions"
                        emit-value
                        use-input
                        fill-input
                    />
                </div>
                <!-- birthdate -->
                <div class="col-12 col-md-3 q-pb-lg">
                    <q-input
                        v-model="selectedBirthDate"
                        outlined
                        clearable
                        :label="$t('general.birthday_date')"
                        bg-color="white"
                        @focus="$refs.birthdayDate.show()"
                        @click="$refs.birthdayDate.show()"
                    >
                        <template v-slot:append>
                            <q-icon name="event" class="cursor-pointer">
                                <q-popup-proxy
                                    ref="birthdayDate"
                                    transition-show="scale"
                                    transition-hide="scale"
                                >
                                    <q-date
                                        v-model="user.birthdate"
                                        mask="YYYY-MM-DD"
                                        :locale="
                                            locale.definition
                                                .calendarPickerLocale
                                                ? locale.definition
                                                      .calendarPickerLocale
                                                : null
                                        "
                                        @input="() => $refs.birthdayDate.hide()"
                                    />
                                </q-popup-proxy>
                            </q-icon>
                        </template>
                    </q-input>
                </div>
                <!-- phone number -->
                <div class="col-12 col-md-3 q-pb-lg">
                    <q-input
                        v-model="user.phone"
                        outlined
                        clearable
                        counter
                        maxlength="25"
                        :label="$t('general.phone')"
                        bg-color="white"
                    />
                </div>
                <!-- tax number -->
                <div class="col-12 col-md-3 q-pb-lg">
                    <q-input
                        v-model="user.tax_number"
                        :label="$t('general.tax_number')"
                        type="text"
                        bg-color="white"
                        outlined
                        clearable
                        counter
                        maxlength="15"
                        :error-message="taxNumberErrors"
                        :error="taxNumberErrors != ''"
                        @input="$v.user.tax_number.$touch()"
                        @blur="$v.user.tax_number.$touch()"
                    />
                </div>
                <!-- currency -->
                <div class="col-12 col-md-3 q-pb-lg">
                    <q-select
                        v-model="selectedCurrency"
                        class="q-mb-md"
                        :label="$t('general.currency')"
                        bg-color="white"
                        :options="currencyOptions"
                        option-value="id"
                        option-label="name"
                        clearable
                        outlined
                        :error-message="currencyErrors"
                        :error="currencyErrors != ''"
                        @input="$v.selectedCurrency.$touch()"
                        @blur="$v.selectedCurrency.$touch()"
                    >
                        <template v-if="selectedCurrency" v-slot:selected>
                            <span class="text-uppercase text-black">
                                {{ selectedCurrency.key }}</span
                            >
                        </template>
                        <template v-slot:option="scope">
                            <q-item
                                v-bind="scope.itemProps"
                                v-on="scope.itemEvents"
                            >
                                <q-item-section>
                                    <q-item-label class="text-capitalize"
                                        >{{ scope.opt.name }}
                                        <span class="text-uppercase"
                                            >({{ scope.opt.key }})</span
                                        >
                                    </q-item-label>
                                </q-item-section>
                            </q-item>
                        </template>
                    </q-select>
                </div>
                <!-- Country select -->
                <div class="col-12 col-md-3 q-pb-lg">
                    <q-select
                        v-model="user.country_id"
                        class="q-mb-md"
                        :label="$t('general.country')"
                        bg-color="white"
                        hide-selected
                        outlined
                        input-debounce="0"
                        option-value="id"
                        option-label="name"
                        map-options
                        :options="filteredCountries"
                        emit-value
                        use-input
                        fill-input
                        clearable
                        :disable="hasErpIntegration"
                        :error-message="countryErrors"
                        :error="countryErrors != ''"
                        @filter="filterCountries"
                        @input="$v.user.country_id.$touch()"
                        @blur="$v.user.country_id.$touch()"
                    />
                </div>
                <!-- street -->
                <div class="col-12 col-md-6 q-pb-lg">
                    <q-input
                        v-model="user.street"
                        outlined
                        clearable
                        counter
                        maxlength="35"
                        :label="$t('general.street')"
                        bg-color="white"
                        :error-message="streetErrors"
                        :error="streetErrors != ''"
                        @input="$v.user.street.$touch()"
                        @blur="$v.user.street.$touch()"
                    />
                </div>
                <!-- city -->
                <div class="col-12 col-md-6 q-pb-lg">
                    <q-input
                        v-model="user.city"
                        outlined
                        clearable
                        counter
                        maxlength="100"
                        :label="$t('general.city')"
                        bg-color="white"
                        :error-message="cityErrors"
                        :error="cityErrors != ''"
                        @input="$v.user.city.$touch()"
                        @blur="$v.user.city.$touch()"
                    />
                </div>
                <!-- state -->
                <div class="col-6 q-pb-lg">
                    <q-input
                        v-model="user.state"
                        outlined
                        clearable
                        counter
                        maxlength="35"
                        :label="$t('general.state')"
                        bg-color="white"
                        :error-message="stateErrors"
                        :error="stateErrors != ''"
                        @input="$v.user.state.$touch()"
                        @blur="$v.user.state.$touch()"
                    />
                </div>
                <!-- zip code -->
                <div class="col-6 q-pb-lg">
                    <q-input
                        v-model="user.zip_code"
                        outlined
                        clearable
                        counter
                        maxlength="20"
                        :label="$t('general.zip_code')"
                        bg-color="white"
                    />
                </div>
                <!-- occupation -->
                <div class="col-12 q-pb-lg">
                    <q-input
                        v-model="user.occupation"
                        outlined
                        clearable
                        counter
                        maxlength="150"
                        :label="$t('general.occupation')"
                        bg-color="white"
                    />
                </div>
                <!-- summary -->
                <div class="col">
                    <q-editor
                        v-model="user.summary"
                        class="editor"
                        toolbar-bg="grey-3"
                        min-height="5rem"
                    />
                </div>
            </div>

            <div class="col self-end">
                <q-btn
                    color="secondary"
                    :label="$t('actions.save')"
                    :disable="isSaving"
                    @click="savePersonnalData"
                />
            </div>
        </div>
    </div>
</template>

<script>
import translate from '@/local-shared/mixins/translate'
import validations from '@/views/profile/my-profile/mixins/validations'
import usersService from '@/services/modules/users'
import gendersService from '@/services/modules/profile/genders'
import countriesService from '@/services/modules/profile/countries'
import notify from '@/local-shared/mixins/notify'
import store from '@/store'
import formatDate from '@/helpers/date-time'
import currenciesService from '@/services/modules/currencies'
import { mapGetters } from 'vuex'

const loadingKeys = {
    savingData: 'saving-data',
    fetchingUser: 'fetching-user',
    fetchingGenders: 'fetching-genders',
    fetchingCountries: 'fetching-countries',
    fetchingCurrencies: 'fetching-currencies',
}

export default {
    name: 'MyProfileView',

    components: {},

    mixins: [translate, validations, notify],

    data() {
        return {
            user: {
                first_name: '',
                last_name: '',
                phone: '',
                tax_number: '',
                street: '',
                city: '',
                state: '',
                zip_code: '',
                occupation: '',
                summary: '',
                gender_id: null,
                birthdate: '',
                userConfiguration: null,
            },

            genderOptions: [],

            countryOptions: [],
            filteredCountries: [],

            currencyOptions: [],
            selectedCurrency: null,
        }
    },

    computed: {
        ...mapGetters('integrations', ['hasErpIntegration']),

        locale() {
            return store.getters['locale/currentLocale']
        },

        selectedBirthDate() {
            return this.user.birthdate
                ? formatDate.fromDatePickerToUxDate(this.user.birthdate)
                : null
        },

        isSaving() {
            return this.isSavingData
        },

        isSavingData() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.savingData
            ).length
        },

        isLoading() {
            return (
                this.isFetchingUser ||
                this.isFetchingGenders ||
                this.isFetchingCountries
            )
        },

        isFetchingUser() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingUser
            ).length
        },

        isFetchingGenders() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingGenders
            ).length
        },

        isFetchingCountries() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingCountries
            ).length
        },

        storedUser() {
            return store.getters['session/user']
        },

        selectedGender: {
            get() {
                const rs = this.genderOptions.filter(
                    (row) => row.id === this.userAux.gender_id
                )
                return rs.length === 1 ? rs[0] : rs
            },
            set(v) {
                this.userAux.gender_id = v.id
            },
        },
    },

    async created() {
        await Promise.all([
            this.fetchGenders(),
            this.fetchCountries(),
            this.fetchCurrencies(),
        ])
        await this.fetchUser()
    },

    methods: {
        async savePersonnalData() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                return true
            }

            this.loadingAdd(loadingKeys.savingData)

            const { userConfiguration, ...userData } = this.user

            let parameters = {
                data: [{ ...userData, currency: this.selectedCurrency }],
            }

            let response = await usersService.update(parameters)

            if (response.status === 200) {
                this.$q.notify({
                    type: 'positive',
                    message: this.$t('notify.profile_updated'),
                })
                const userInStore = store.getters['session/user']
                const mergedUser = Object.assign({}, userInStore, userData)
                store.dispatch('session/setUser', { ...mergedUser })
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('notify.error_occurred', {
                        message: response.data.message,
                    }),
                })
            }

            this.loadingRemove(loadingKeys.savingData)
        },

        async fetchUser() {
            this.loadingAdd(loadingKeys.fetchingUser)

            let parameters = {
                filter: {
                    id: this.storedUser.id,
                },
                relations: {
                    affiliations: true,
                    userConfiguration: true,
                },
            }

            let response = await usersService.retrieve(parameters)

            if (response.status === 200) {
                this.user = response.data.results[0]
                this.user.summary = this.user.summary || '' // prevent Null value from database

                let userCurrencyId = this.user?.userConfiguration
                    ? JSON.parse(this.user.userConfiguration.configuration)[
                          'currency'
                      ]
                    : null

                this.selectedCurrency = userCurrencyId
                    ? this.currencyOptions.find(
                          (currency) => currency.id === userCurrencyId
                      ) || null
                    : null
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('notify.error_occurred', {
                        message: response.data.message,
                    }),
                })
            }

            this.loadingRemove(loadingKeys.fetchingUser)
        },

        async fetchGenders() {
            this.loadingAdd(loadingKeys.fetchingGenders)

            let parameters = {
                filter: {},
            }

            let response = await gendersService.retrieve(parameters)

            if (response.status === 200) {
                let genderOptions = response.data.results
                await Promise.all(
                    genderOptions.map((item) => {
                        item.name = this.JSONParseTranslations(
                            item.name,
                            this.locale.key
                        )
                    })
                )
                this.genderOptions = genderOptions
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingGenders)
        },

        async fetchCountries() {
            this.loadingAdd(loadingKeys.fetchingCountries)

            let parameters = {
                filter: {},
            }

            let response = await countriesService.retrieve(parameters)

            if (response.status === 200) {
                this.countryOptions = response.data.results
                this.filteredCountries = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingCountries)
        },

        filterCountries(val, update) {
            update(() => {
                const needle = val.toLowerCase()
                this.filteredCountries = this.countryOptions.filter(
                    (v) => v.name.toLowerCase().indexOf(needle) > -1
                )
            })
        },

        async fetchCurrencies() {
            this.loadingAdd(loadingKeys.fetchingCurrencies)

            let parameters = {
                filter: {},
            }

            let response = await currenciesService.retrieve(parameters)

            if (response.status === 200) {
                this.currencyOptions = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingCurrencies)
        },
    },
}
</script>
